<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>Withdrawals</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input v-model="query" type="text" placeholder="Search name, phone, requestId, userId..." />
        </div>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="PENDING">Pending</option>
          <option value="APPROVED">Approved</option>
          <option value="REJECTED">Rejected</option>
          <option value="SETTLED">Settled</option>
        </select>

        <select v-model="methodFilter" class="select">
          <option value="">All Methods</option>
          <option value="BANK">Bank Transfer</option>
          <option value="UPI">UPI</option>
        </select>

        <select v-model="sortBy" class="select">
          <option value="NEWEST">Newest First</option>
          <option value="OLDEST">Oldest First</option>
          <option value="AMOUNT_DESC">Amount High → Low</option>
          <option value="AMOUNT_ASC">Amount Low → High</option>
        </select>

        <button class="btn ghost" @click="exportXl">
          <i class="bx bx-export"></i>
          Export
        </button>
      </div>
    </div>

    <!-- ✅ WALLET SUMMARY (FULL WIDTH) -->
    <div class="wallet-full" style="margin-top: 14px;">
      <div class="wallet-card">
        <div class="wc-head">
          <div class="wc-title">
            <div class="chip">
              <i class="bx bx-wallet"></i>
              <span>Wallet Summary</span>
            </div>
            <strong>Super Admin Wallet</strong>
            <span class="row-muted">Operational pool used for settlements</span>
          </div>

          <button class="icon-btn" @click="toast.info('Sync wallet API later')" title="Refresh">
            <i class="bx bx-refresh"></i>
          </button>
        </div>

        <div class="wc-metrics">
          <div class="mitem">
            <span class="k">Available Balance</span>
            <strong class="v">₹ {{ fmt(availableBalance) }}</strong>
            <span class="tag success"><i class="bx bx-check-circle"></i> Ready</span>
          </div>

          <div class="mitem">
            <span class="k">Pending Amount</span>
            <strong class="v">₹ {{ fmt(pendingAmount) }}</strong>
            <span class="tag warning"><i class="bx bx-time-five"></i> In queue</span>
          </div>

          <div class="mitem">
            <span class="k">Settled (30d)</span>
            <strong class="v">₹ {{ fmt(settled30d) }}</strong>
            <span class="tag primary"><i class="bx bx-trending-up"></i> Stable</span>
          </div>
        </div>
      </div>

      <!-- ✅ BUTTONS BELOW WALLET CARD (ROW) -->
      <div class="wallet-actions-row">
        <button class="btn primary" @click="openFund">
          <i class="bx bx-plus-circle"></i>
          Add Funds
        </button>

        <button class="btn ghost" @click="toast.info('Open ledger screen later')">
          <i class="bx bx-receipt"></i>
          View Ledger
        </button>

        <button class="btn ghost" @click="toast.info('Wallet history later')">
          <i class="bx bx-history"></i>
          Wallet History
        </button>
      </div>
    </div>

    <!-- ✅ KPI CARDS BELOW WALLET -->
    <div class="kpi-grid grid cols-4" style="margin-top: 14px;">
      <div class="card stat">
        <div class="card-title">
          <strong>Pending Requests</strong>
          <span class="badge warning">ACTION</span>
        </div>
        <div class="metric">{{ pendingCount }}</div>
        <div class="mini">Awaiting approval</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Approved</strong>
          <span class="badge primary">QUEUE</span>
        </div>
        <div class="metric">{{ approvedCount }}</div>
        <div class="mini">Ready to settle</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Settled</strong>
          <span class="badge success">DONE</span>
        </div>
        <div class="metric">{{ settledCount }}</div>
        <div class="mini">Paid requests</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Rejected</strong>
          <span class="badge warning">CHECK</span>
        </div>
        <div class="metric">{{ rejectedCount }}</div>
        <div class="mini">Need resubmission</div>
      </div>
    </div>

    <!-- LIST -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>Withdrawal Requests</strong>
          <span class="muted">Showing {{ filtered.length }} records</span>
        </div>
      </div>

      <!-- Desktop table -->
      <div class="table-wrap desktop-only">
        <table class="table">
          <thead>
            <tr>
              <th>REQUEST</th>
              <th>USER</th>
              <th>METHOD</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>REQUESTED</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="w in filtered" :key="w.requestId">
              <td>
                <div class="req">
                  <strong>#{{ w.requestId }}</strong>
                  <span class="row-muted">User #{{ w.userId }}</span>
                </div>
              </td>

              <td>
                <div class="usr">
                  <div class="avatar">{{ w.name.charAt(0).toUpperCase() }}</div>
                  <div class="usr-meta">
                    <div class="usr-name">{{ w.name }}</div>
                    <div class="row-muted">{{ w.phone }}</div>
                  </div>
                </div>
              </td>

              <td>
                <span class="method-pill">
                  <i class="bx" :class="w.method === 'BANK' ? 'bx-bank' : 'bx-credit-card'"></i>
                  {{ w.method === "BANK" ? "Bank Transfer" : "UPI" }}
                </span>
                <div class="row-muted">
                  {{ w.method === "BANK" ? w.bankName : w.upiId }}
                </div>
              </td>

              <td>
                <div class="amt">
                  <strong>₹ {{ fmt(w.amount) }}</strong>
                  <span class="row-muted">Fee: ₹ {{ fmt(w.fee) }}</span>
                </div>
              </td>

              <td>
                <span class="status-pill" :class="statusClass(w.status)">
                  <i class="bx" :class="statusIcon(w.status)"></i>
                  {{ w.status }}
                </span>
              </td>

              <td class="row-muted">{{ w.requestedAt }}</td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="icon-mini" @click="openDrawer(w)" title="Details">
                    <i class="bx bx-show"></i>
                  </button>

                  <button
                    v-if="w.status === 'PENDING'"
                    class="icon-mini"
                    @click="openApprove(w)"
                    title="Approve"
                  >
                    <i class="bx bx-check-circle"></i>
                  </button>

                  <button
                    v-if="w.status === 'PENDING'"
                    class="icon-mini danger"
                    @click="openReject(w)"
                    title="Reject"
                  >
                    <i class="bx bx-x-circle"></i>
                  </button>

                  <button
                    v-if="w.status === 'APPROVED'"
                    class="icon-mini"
                    @click="openSettle(w)"
                    title="Settle"
                  >
                    <i class="bx bx-send"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="filtered.length === 0">
              <td colspan="7" style="padding:18px;">
                <div class="empty">
                  <i class="bx bx-file-find"></i>
                  <div>
                    <strong>No withdrawal requests found</strong>
                    <div class="row-muted">Try adjusting search/filters.</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="mobile-only list-cards">
        <div v-for="w in filtered" :key="w.requestId" class="w-card">
          <div class="top">
            <div class="req">
              <strong>#{{ w.requestId }}</strong>
              <span class="row-muted">User #{{ w.userId }}</span>
            </div>

            <span class="status-pill" :class="statusClass(w.status)">
              <i class="bx" :class="statusIcon(w.status)"></i>
              {{ w.status }}
            </span>
          </div>

          <div class="mid">
            <div class="usr">
              <div class="avatar">{{ w.name.charAt(0).toUpperCase() }}</div>
              <div class="usr-meta">
                <div class="usr-name">{{ w.name }}</div>
                <div class="row-muted">{{ w.phone }}</div>
              </div>
            </div>

            <div class="row2">
              <span class="method-pill">
                <i class="bx" :class="w.method === 'BANK' ? 'bx-bank' : 'bx-credit-card'"></i>
                {{ w.method === "BANK" ? "Bank Transfer" : "UPI" }}
              </span>

              <div class="amt">
                <strong>₹ {{ fmt(w.amount) }}</strong>
                <span class="row-muted">Fee: ₹ {{ fmt(w.fee) }}</span>
              </div>
            </div>

            <div class="row-muted">Requested: {{ w.requestedAt }}</div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openDrawer(w)">
              <i class="bx bx-show"></i> Details
            </button>

            <button v-if="w.status==='PENDING'" class="btn ghost" @click="openApprove(w)">
              <i class="bx bx-check-circle"></i> Approve
            </button>

            <button v-if="w.status==='PENDING'" class="btn ghost" @click="openReject(w)">
              <i class="bx bx-x-circle"></i> Reject
            </button>

            <button v-if="w.status==='APPROVED'" class="btn ghost" @click="openSettle(w)">
              <i class="bx bx-send"></i> Settle
            </button>
          </div>
        </div>

        <div v-if="filtered.length === 0" class="w-card">
          <div class="empty">
            <i class="bx bx-file-find"></i>
            <div>
              <strong>No withdrawal requests found</strong>
              <div class="row-muted">Try adjusting search/filters.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DETAILS DRAWER -->
    <div v-if="drawer.open" class="drawer-backdrop" @click.self="closeDrawer">
      <aside class="drawer" v-motion-pop>
        <div class="drawer-head">
          <div class="drawer-title">
            <strong>Request Details</strong>
            <span class="row-muted">Withdrawal audit timeline</span>
          </div>
          <button class="icon-mini" @click="closeDrawer"><i class="bx bx-x"></i></button>
        </div>

        <div class="drawer-body">
          <div class="detail-card">
            <div class="dtop">
              <div class="req">
                <strong>#{{ drawer.req?.requestId }}</strong>
                <span class="row-muted">User #{{ drawer.req?.userId }}</span>
              </div>

              <span class="status-pill" :class="statusClass(drawer.req?.status)">
                <i class="bx" :class="statusIcon(drawer.req?.status)"></i>
                {{ drawer.req?.status }}
              </span>
            </div>

            <div class="dgrid">
              <div class="kv">
                <span class="k">User</span>
                <strong class="v">{{ drawer.req?.name }}</strong>
                <span class="row-muted">{{ drawer.req?.phone }}</span>
              </div>

              <div class="kv">
                <span class="k">Amount</span>
                <strong class="v">₹ {{ fmt(drawer.req?.amount || 0) }}</strong>
                <span class="row-muted">Fee: ₹ {{ fmt(drawer.req?.fee || 0) }}</span>
              </div>

              <div class="kv">
                <span class="k">Method</span>
                <strong class="v">{{ drawer.req?.method === 'BANK' ? "Bank Transfer" : "UPI" }}</strong>
                <span class="row-muted">
                  {{ drawer.req?.method === 'BANK' ? "Bank: "+drawer.req?.bankName : drawer.req?.upiId }}
                </span>
                <br>
                <span class="row-muted" v-if="drawer.req?.method === 'BANK'">
                  Account No: {{ drawer.req?.accountNo }}
                </span>
                <br>
                <span class="row-muted" v-if="drawer.req?.method === 'BANK'">
                  IFSC: {{ drawer.req?.ifsc }}
                </span>
              </div>

              <div class="kv">
                <span class="k">Requested</span>
                <strong class="v">{{ drawer.req?.requestedAt }}</strong>
                <span class="row-muted">Approval: {{ drawer.req?.approvedAt || "-" }}</span>
              </div>
            </div>

            <div class="timeline">
              <div class="titem">
                <div class="dot done"></div>
                <div>
                  <strong>Requested</strong>
                  <div class="row-muted">{{ drawer.req?.requestedAt }}</div>
                </div>
              </div>

              <div class="titem">
                <div class="dot" :class="drawer.req?.status !== 'PENDING' ? 'done' : ''"></div>
                <div>
                  <strong>Reviewed</strong>
                  <div class="row-muted">
                    {{ drawer.req?.reviewedBy || "Not reviewed yet" }}
                  </div>
                </div>
              </div>

              <div class="titem">
                <div class="dot" :class="drawer.req?.status === 'SETTLED' ? 'done' : ''"></div>
                <div>
                  <strong>Settled</strong>
                  <div class="row-muted">
                    {{ drawer.req?.settledAt || "Not settled yet" }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="drawer.req?.status === 'REJECTED'" class="reject-box">
              <i class="bx bx-error-circle"></i>
              <div>
                <strong>Rejected Reason</strong>
                <div class="row-muted">{{ drawer.req?.rejectReason || "-" }}</div>
              </div>
            </div>
          </div>

          <div class="qa">
            <button class="btn ghost" @click="toast.info('Open user profile next')">
              <i class="bx bx-user"></i> Open User
            </button>

            <button v-if="drawer.req?.status === 'PENDING'" class="btn primary" @click="openApprove(drawer.req)">
              <i class="bx bx-check-circle"></i> Approve
            </button>

            <button v-if="drawer.req?.status === 'PENDING'" class="btn ghost dangerBtn" @click="openReject(drawer.req)">
              <i class="bx bx-x-circle"></i> Reject
            </button>

            <button v-if="drawer.req?.status === 'APPROVED'" class="btn primary" @click="openSettle(drawer.req)">
              <i class="bx bx-send"></i> Settle
            </button>
          </div>
        </div>
      </aside>
    </div>

    <!-- APPROVE MODAL -->
    <div v-if="approve.open" class="modal-backdrop" @click.self="closeApprove">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Approve Withdrawal</strong>
            <div class="row-muted">Approve request to move it into settlement queue.</div>
          </div>
          <button class="icon-mini" @click="closeApprove"><i class="bx bx-x"></i></button>
        </div>

        <div class="modal-body">
          <div class="hint">
            <i class="bx bx-info-circle"></i>
            <span>
              You are approving request <b>#{{ approve.req?.requestId }}</b> for
              <b>₹ {{ fmt(approve.req?.amount || 0) }}</b>.
            </span>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeApprove">Cancel</button>
          <button class="btn primary" @click="confirmApprove">
            <i class="bx bx-check-circle"></i> Approve
          </button>
        </div>
      </div>
    </div>

    <!-- REJECT MODAL -->
    <div v-if="reject.open" class="modal-backdrop" @click.self="closeReject">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Reject Withdrawal</strong>
            <div class="row-muted">Reason required for audit trail.</div>
          </div>
          <button class="icon-mini" @click="closeReject"><i class="bx bx-x"></i></button>
        </div>

        <div class="modal-body">
          <div class="field">
            <label>Reject Reason</label>
            <textarea
              class="notes"
              v-model="reject.reason"
              placeholder="Example: Invalid bank details / mismatch / suspicious activity..."
            ></textarea>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeReject">Cancel</button>
          <button class="btn primary" :disabled="!reject.reason.trim()" @click="confirmReject">
            <i class="bx bx-x-circle"></i> Reject
          </button>
        </div>
      </div>
    </div>

    <!-- SETTLE MODAL -->
    <div v-if="settle.open" class="modal-backdrop" @click.self="closeSettle">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Settle Withdrawal</strong>
            <div class="row-muted">Enter settlement transaction details.</div>
          </div>
          <button class="icon-mini" @click="closeSettle"><i class="bx bx-x"></i></button>
        </div>

        <div class="modal-body">
          <div class="hint">
            <i class="bx bx-info-circle"></i>
            <span>
              Settling request <b>#{{ settle.req?.requestId }}</b> of
              <b>₹ {{ fmt(settle.req?.amount || 0) }}</b>.
            </span>
          </div>

          <div class="field">
            <label>Transaction ID</label>
            <div class="input">
              <i class="bx bx-receipt"></i>
              <input v-model="settle.txnId" placeholder="TXN123456..." />
            </div>
          </div>

          <div class="field">
            <label>Bank Reference / UTR</label>
            <div class="input">
              <i class="bx bx-transfer"></i>
              <input v-model="settle.utr" placeholder="UTR / Ref number" />
            </div>
          </div>

          <div class="field">
            <label>Paid Date</label>
            <div class="input">
              <i class="bx bx-calendar"></i>
              <input v-model="settle.paidAt" type="date" />
            </div>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closeSettle">Cancel</button>
          <button class="btn primary" :disabled="!canSettle" @click="confirmSettle">
            <i class="bx bx-send"></i> Mark Settled
          </button>
        </div>
      </div>
    </div>
    <!-- ADD FUNDS MODAL -->
<div v-if="fund.open" class="modal-backdrop" @click.self="closeFund">
  <div class="modal" v-motion-pop>
    <div class="modal-head">
      <div>
        <strong>Add Funds</strong>
        <div class="row-muted">Top-up super admin wallet balance</div>
      </div>
      <button class="icon-mini" @click="closeFund"><i class="bx bx-x"></i></button>
    </div>

    <div class="modal-body">
      <div class="field">
        <label>Amount</label>
        <div class="input">
          <i class="bx bx-rupee"></i>
          <input v-model="fund.amount" type="number" placeholder="Enter amount" />
        </div>
      </div>

      <div class="field">
        <label>Method</label>
        <div class="input">
          <i class="bx bx-credit-card"></i>

          <select v-model="fund.method" class="select full">
            <option value="cash">Cash</option>
            <option value="bank">Bank Transfer</option>
            <option value="upi">UPI</option>
            <option value="other">Other</option>
          </select>

          <!-- ✅ dropdown arrow -->
          <i class="bx bx-chevron-down dd"></i>
        </div>
      </div>


      <div class="field">
        <label>Reference (optional)</label>
        <div class="input">
          <i class="bx bx-receipt"></i>
          <input v-model="fund.ref" placeholder="UTR / slip / txn id" />
        </div>
      </div>

      <div class="field">
        <label>Remark (optional)</label>
        <textarea class="notes" v-model="fund.remark" placeholder="Example: wallet topup for settlements"></textarea>
      </div>

      <div class="hint">
        <i class="bx bx-info-circle"></i>
        <span>Funds will be added instantly and reflected in wallet summary.</span>
      </div>
    </div>

    <div class="modal-foot">
      <button class="btn ghost" @click="closeFund">Cancel</button>
      <button class="btn primary" :disabled="fund.loading" @click="submitFund">
        <span v-if="fund.loading" class="loader" style="margin-right:8px;"></span>
        <i v-else class="bx bx-plus-circle"></i>
        {{ fund.loading ? "Adding..." : "Add Funds" }}
      </button>
    </div>
  </div>
</div>

  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted } from "vue";
import { useToast } from "vue-toastification";

import {
  getWalletSummary,
  getWithdrawals,
  approveWithdrawal,
  rejectWithdrawal,
  settleWithdrawal,
  exportWithdrawalsXl,
  addFunds
} from "@/apis/withcon";

const toast = useToast();

/* ✅ admin data */
const ldata = ref(null);
const admin_id = ref("");

/* ✅ loaders */
const loading = reactive({
  wallet: false,
  list: false,
  export: false,
});

/* filters */
const query = ref("");
const statusFilter = ref("");
const methodFilter = ref("");
const sortBy = ref("NEWEST");

/* api records */
const requests = ref([]);

/* wallet */
const availableBalance = ref(0);
const pendingAmount = ref(0);
const settled30d = ref(0);

/* helpers */
const fmt = (n) => new Intl.NumberFormat("en-IN").format(Number(n || 0));

const statusClass = (s) => {
  s = String(s || "").toUpperCase();
  if (s === "SETTLED") return "success";
  if (s === "APPROVED") return "primary";
  if (s === "PENDING") return "warning";
  if (s === "REJECTED") return "danger";
  return "primary";
};

const statusIcon = (s) => {
  s = String(s || "").toUpperCase();
  if (s === "SETTLED") return "bx-check-circle";
  if (s === "APPROVED") return "bx-check-shield";
  if (s === "PENDING") return "bx-time-five";
  if (s === "REJECTED") return "bx-x-circle";
  return "bx-info-circle";
};

/* stats */
const pendingCount = computed(() => requests.value.filter((x) => x.status === "PENDING").length);
const approvedCount = computed(() => requests.value.filter((x) => x.status === "APPROVED").length);
const settledCount = computed(() => requests.value.filter((x) => x.status === "SETTLED").length);
const rejectedCount = computed(() => requests.value.filter((x) => x.status === "REJECTED").length);

/* ===========================
   Add Funds Modal
   =========================== */
const fund = reactive({
  open: false,
  loading: false,
  amount: "",
  method: "cash",
  ref: "",
  remark: "",
});

const openFund = () => {
  fund.open = true;
  fund.amount = "";
  fund.method = "cash";
  fund.ref = "";
  fund.remark = "";
};

const closeFund = () => {
  fund.open = false;
};

const submitFund = async () => {
  try {
    const amt = String(fund.amount || "").trim();

    if (!amt || !Number(amt) || Number(amt) <= 0) {
      toast.error("Enter valid amount");
      return;
    }

    fund.loading = true;

    const res = await addFunds({
      admin_id: admin_id.value,
      amount: amt,
      method: fund.method,
      ref: fund.ref,
      remark: fund.remark,
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to add funds");
      return;
    }

    toast.success("Funds added ✅");
    closeFund();
    await reloadAll();
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    fund.loading = false;
  }
};

/* ✅ IMPORTANT: normalize API records so UI never crashes */
const normalizeWithdrawal = (x) => {
  return {

    /* ---------- IDS ---------- */

    requestId: x.withdraw_id || "",
    userId: x.withdraw_user || x.user_id || "",
    loanId: x.withdraw_loan || "",

    /* ---------- USER ---------- */

    name:
      x.user_fname
        ? x.user_fname + " " + (x.user_lname || "")
        : "Unknown User",

    phone: x.user_mobile || "-",

    /* ---------- METHOD ---------- */

    method: (() => {
      const m = String(x.withdraw_method || "").toLowerCase();
      if (m.includes("bank")) return "BANK";
      return "UPI";
    })(),

    /* ---------- STATUS ---------- */

    status: (() => {
      const s = String(x.withdraw_status || "").toLowerCase();

      if (s === "pending") return "PENDING";
      if (s === "processing") return "APPROVED";
      if (s === "completed") return "SETTLED";
      if (s === "rejected") return "REJECTED";

      return "PENDING";
    })(),

    /* ---------- PAYOUT ---------- */

    bankName: x.withdraw_bank_name || "",
    accountNo: x.withdraw_account_no || "",
    ifsc: x.withdraw_ifsc || "",
    upiId: x.withdraw_upi || "",
    note: x.withdraw_note || "",

    /* ---------- AMOUNT ---------- */

    amount: Number(x.withdraw_amount || 0),
    fee: 0,

    /* ---------- ACTION ---------- */

    reviewedBy: x.withdraw_admin || "",
    actionAt: x.withdraw_action_at || "",
    requestedAt: x.withdraw_created_at || "",

    /* ---------- SETTLEMENT ---------- */

    txnId: x.txnId || "",
    utr: x.utr || "",
    rejectReason: x.withdraw_note || "",
  };
};



/* filtering */
const filtered = computed(() => {
  let list = requests.value.filter((w) => {
    const q = query.value.toLowerCase();

    const matchesQ =
      !q ||
      String(w.requestId || "").includes(q) ||
      String(w.userId || "").includes(q) ||
      String(w.name || "").toLowerCase().includes(q) ||
      String(w.phone || "").toLowerCase().includes(q);

    const matchesStatus = !statusFilter.value || w.status === statusFilter.value;
    const matchesMethod = !methodFilter.value || w.method === methodFilter.value;

      return matchesQ && matchesStatus && matchesMethod;
    });

    if (sortBy.value === "NEWEST") list = list.slice();
    if (sortBy.value === "OLDEST") list = list.slice().reverse();

    if (sortBy.value === "AMOUNT_DESC")
      list = list.slice().sort((a, b) => (b.amount || 0) - (a.amount || 0));
    if (sortBy.value === "AMOUNT_ASC")
      list = list.slice().sort((a, b) => (a.amount || 0) - (b.amount || 0));

    return list;
  });

/* ===========================
   API CALLS
   =========================== */

const fetchWallet = async () => {
  try {
    loading.wallet = true;
    const res = await getWalletSummary(admin_id.value);
    if (!res || !res.status) return;

    availableBalance.value = Number(res.available_balance || 0);
    pendingAmount.value = Number(res.pending_amount || 0);
    settled30d.value = Number(res.settled_30d || 0);
  } catch (e) {
    // ignore
  } finally {
    loading.wallet = false;
  }
};

const fetchList = async () => {
  try {
    loading.list = true;

    const res = await getWithdrawals({
      admin_id: admin_id.value,
      page_no: 0,
      keyword: "",
      statusFilter: "",
      methodFilter: "",
      sortBy: "NEWEST",
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to load withdrawals");
      return;
    }

    const list = (res.records || []).map(normalizeWithdrawal);

    // ✅ prevent crash
    requests.value = list.filter((x) => String(x.requestId).length > 0);

  } catch (e) {
    toast.error("Request Failed");
  } finally {
    loading.list = false;
  }
};

const reloadAll = async () => {
  await fetchWallet();
  await fetchList();
};

/* drawer */
const drawer = reactive({ open: false, req: null });
const openDrawer = (req) => {
  drawer.open = true;
  drawer.req = req;
};
const closeDrawer = () => {
  drawer.open = false;
  drawer.req = null;
};

/* approve */
const approve = reactive({ open: false, req: null });
const openApprove = (req) => {
  approve.open = true;
  approve.req = req;
};
const closeApprove = () => {
  approve.open = false;
  approve.req = null;
};

const confirmApprove = async () => {
  try {
    if (!approve.req?.requestId) return;

    const res = await approveWithdrawal(approve.req.requestId, admin_id.value);
    if (!res || !res.status) return toast.error(res?.success || "Failed");

    toast.success("Withdrawal approved ✅");
    closeApprove();
    await reloadAll();
  } catch (e) {
    toast.error("Request Failed");
  }
};

/* reject */
const reject = reactive({ open: false, req: null, reason: "" });
const openReject = (req) => {
  reject.open = true;
  reject.req = req;
  reject.reason = "";
};
const closeReject = () => {
  reject.open = false;
  reject.req = null;
  reject.reason = "";
};

const confirmReject = async () => {
  try {
    if (!reject.req?.requestId) return;
    if (!reject.reason.trim()) return toast.error("Enter reject reason");

    const res = await rejectWithdrawal(reject.req.requestId, admin_id.value, reject.reason.trim());
    if (!res || !res.status) return toast.error(res?.success || "Failed");

    toast.success("Withdrawal rejected ✅");
    closeReject();
    await reloadAll();
  } catch (e) {
    toast.error("Request Failed");
  }
};

/* settle */
const settle = reactive({
  open: false,
  req: null,
  txnId: "",
  utr: "",
  paidAt: "",
});

const openSettle = (req) => {
  settle.open = true;
  settle.req = req;
  settle.txnId = "";
  settle.utr = "";
  settle.paidAt = new Date().toISOString().slice(0, 10);
};

const closeSettle = () => {
  settle.open = false;
  settle.req = null;
  settle.txnId = "";
  settle.utr = "";
  settle.paidAt = "";
};

const canSettle = computed(() => {
  return !!(settle.txnId.trim() && settle.utr.trim() && settle.paidAt);
});

const confirmSettle = async () => {
  try {
    if (!settle.req?.requestId) return;

    const res = await settleWithdrawal({
      withdraw_id: settle.req.requestId,
      admin_id: admin_id.value,
      txn_id: settle.txnId.trim(),
      utr: settle.utr.trim(),
      paid_at: settle.paidAt,
    });

    if (!res || !res.status) return toast.error(res?.success || "Failed");

    toast.success("Marked as settled ✅");
    closeSettle();
    await reloadAll();
  } catch (e) {
    toast.error("Request Failed");
  }
};

/* export xl */
const exportXl = async () => {
  try {
    loading.export = true;

    const res = await exportWithdrawalsXl({
      admin_id: admin_id.value,
      keyword: query.value || "",
      statusFilter: statusFilter.value || "",
      methodFilter: methodFilter.value || "",
      sortBy: sortBy.value || "NEWEST",
    });

    if (!res) {
      toast.error("Export failed");
      return;
    }

    // ✅ axios returns blob in res.data
    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `withdrawals_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    toast.success("Export downloaded ✅");
  } catch (e) {
    toast.error("Export failed");
  } finally {
    loading.export = false;
  }
};

/* mount */
onMounted(async () => {
  try {
    ldata.value = JSON.parse(sessionStorage.getItem("ldata") || "{}");

    // ✅ IMPORTANT: do NOT use email as admin_id
    // use stored admin_id if available
    admin_id.value =
      ldata.value?.admin_id ||
      ldata.value?.user_id ||
      ldata.value?.id ||
      "1";

    await reloadAll();
  } catch (e) {
    // ignore
  }
});
</script>


<style scoped>
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-mini {
  height: 44px;
  position: relative;
  min-width: 280px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

/* ✅ NEW FULL WIDTH WALLET */
.wallet-full {
  width: 100%;
  display: grid;
  gap: 12px;
}

.wallet-card {
  width: 100%;
  border-radius: 26px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: var(--shadow-sm);
  padding: 16px;
  position: relative;
  overflow: hidden;
}

.wallet-card:before {
  content: "";
  position: absolute;
  inset: -20% -35% auto -35%;
  height: 260px;
  border-radius: 999px;
  background: radial-gradient(
    closest-side,
    rgba(46, 109, 231, 0.22),
    transparent 70%
  );
  filter: blur(2px);
}

.wc-head {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.wc-title strong {
  display: block;
  margin-top: 10px;
  font-size: 18px;
  font-weight: var(--w-black);
  letter-spacing: -0.3px;
}

.wc-title .row-muted {
  margin-top: 6px;
}

.chip {
  width: fit-content;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 8px 12px;
  border-radius: 999px;
  background: var(--primary-10);
  border: 1px solid rgba(46, 109, 231, 0.22);
  color: var(--primary);
  font-weight: var(--w-semibold);
  font-size: 12px;
}

.chip i {
  font-size: 16px;
}

.wc-metrics {
  position: relative;
  z-index: 1;
  margin-top: 14px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}

.mitem {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  border-radius: 22px;
  padding: 12px;
}

.mitem .k {
  font-size: 12px;
  color: rgba(100, 116, 139, 0.9);
  font-weight: var(--w-medium);
}

.mitem .v {
  display: block;
  margin-top: 8px;
  font-size: 18px;
  font-weight: var(--w-black);
  letter-spacing: -0.2px;
}

.tag {
  margin-top: 10px;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: var(--w-bold);
}

.tag i { font-size: 16px; }

.tag.success { background: var(--success-10); color: var(--success); }
.tag.warning { background: var(--warning-10); color: var(--warning); }
.tag.primary { background: var(--primary-10); color: var(--primary); }

/* ✅ NEW wallet buttons row BELOW card */
.wallet-actions-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.wallet-actions-row .btn {
  width: 100%;
  justify-content: center;
  height: 48px;
  border-radius: 18px;
}

/* KPI grid */
.kpi-grid.cols-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.req { display: grid; gap: 4px; }
.req strong { font-weight: var(--w-black); }

.usr { display: flex; align-items: center; gap: 10px; }
.usr-meta { display: flex; flex-direction: column; }
.usr-name { font-weight: var(--w-bold); font-size: 13px; }

.method-pill {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: var(--w-semibold);
  border: 1px solid var(--line);
  background: rgba(241, 245, 249, 0.65);
  width: fit-content;
}

.method-pill i {
  font-size: 16px;
  color: rgba(100, 116, 139, 0.85);
}

.amt { display: grid; gap: 4px; }
.amt strong { font-weight: var(--w-black); letter-spacing: -0.2px; }

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.table-title { display: flex; flex-direction: column; gap: 4px; }

.muted { color: var(--muted); font-size: 12px; font-weight: var(--w-medium); }

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

.icon-mini:hover { transform: translateY(-1px); }

.icon-mini.danger {
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.18);
}
.icon-mini.danger i { color: var(--danger); }

.empty {
  display: flex;
  gap: 12px;
  align-items: center;
}

.empty i {
  font-size: 26px;
  color: rgba(148, 163, 184, 0.9);
}

.row2 {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

/* mobile cards */
.list-cards {
  display: none;
  margin-top: 10px;
  gap: 12px;
}

.w-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--shadow-sm);
}

.w-card .top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.w-card .mid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.card-actions {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

/* drawer */
.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  z-index: 90;
  display: flex;
  justify-content: flex-end;
}

.drawer {
  width: min(520px, 92vw);
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
  overflow: auto;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.drawer-title strong { font-weight: var(--w-bold); font-size: 16px; }

.drawer-body { margin-top: 14px; display: grid; gap: 14px; }

.detail-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  background: rgba(246, 248, 252, 0.65);
  padding: 14px;
}

.dtop {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.dgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }

.kv {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  border-radius: 18px;
  padding: 10px;
}
.kv .k { font-size: 11px; color: var(--muted); font-weight: var(--w-medium); }
.kv .v { margin-top: 6px; display: block; font-size: 13px; font-weight: var(--w-bold); }

.timeline { margin-top: 12px; display: grid; gap: 12px; }
.titem { display: flex; gap: 12px; align-items: flex-start; }

.dot {
  height: 12px;
  width: 12px;
  border-radius: 999px;
  border: 2px solid rgba(148, 163, 184, 0.6);
  margin-top: 4px;
}
.dot.done {
  border-color: rgba(46, 109, 231, 0.25);
  background: rgba(46, 109, 231, 0.95);
}

.reject-box {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 12px;
  border-radius: 18px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.18);
}
.reject-box i { font-size: 18px; color: var(--danger); margin-top: 2px; }

.qa { display: flex; gap: 10px; flex-wrap: wrap; }

.dangerBtn {
  border: 1px solid rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.08);
  color: rgba(239, 68, 68, 0.95);
}

/* modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 99;
  padding: 18px;
}
.modal {
  width: min(520px, 92vw);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
}
.modal-head { display: flex; justify-content: space-between; gap: 12px; }
.modal-head strong { font-weight: var(--w-bold); font-size: 16px; }
.modal-body { margin-top: 14px; display: grid; gap: 14px; }
.modal-foot { margin-top: 14px; display: flex; gap: 10px; justify-content: flex-end; }

.field label {
  font-size: 12px;
  font-weight: var(--w-semibold);
  color: rgba(15, 23, 42, 0.78);
  margin-bottom: 7px;
  display: block;
}

.input { position: relative; }
.input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.input input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 44px;
  border: 1px solid var(--line);
  border-radius: 18px;
  outline: none;
  background: rgba(255,255,255,0.96);
  font-size: 13px;
  font-weight: var(--w-medium);
}

.input input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.notes {
  width: 100%;
  min-height: 110px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: rgba(255,255,255,0.96);
  padding: 12px 12px;
  outline: none;
  font-size: 13px;
  font-weight: var(--w-medium);
  resize: vertical;
}
.notes:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.hint {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 12px;
  border-radius: 18px;
  background: rgba(46, 109, 231, 0.08);
  border: 1px solid rgba(46, 109, 231, 0.16);
  color: rgba(15, 23, 42, 0.74);
  font-size: 12px;
  font-weight: var(--w-medium);
  line-height: 1.45;
}

.hint i { font-size: 18px; color: var(--primary); margin-top: 1px; }

/* responsive */
@media (max-width: 1100px) {
  .wc-metrics { grid-template-columns: 1fr; }
  .wallet-actions-row { grid-template-columns: 1fr 1fr; }
  .kpi-grid.cols-4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 960px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .list-cards { display: grid; }
  .dgrid { grid-template-columns: 1fr; }
}

@media (max-width: 520px) {
  .wallet-actions-row { grid-template-columns: 1fr; }
  .kpi-grid.cols-4 { grid-template-columns: 1fr; }
}

/* ===========================================
   ✅ MOBILE RESPONSIVE PATCH (GLOBAL SAFE)
   Paste at END of <style scoped>
   =========================================== */

/* ✅ Tablet + Mobile */
@media (max-width: 980px) {
  /* page padding */
  .page {
    padding: 14px 14px 26px !important;
  }

  /* ✅ header becomes stacked */
  .page-head {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  /* ✅ actions -> full width vertical */
  .actions {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .search-mini {
    width: 100% !important;
    min-width: 0 !important;
  }

  .search-mini input {
    width: 100% !important;
  }

  .select {
    width: 100% !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  .actions .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* ✅ wallet section should never overflow */
  .wallet-grid {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }

  .wallet-card {
    width: 100% !important;
    padding: 14px !important;
    border-radius: 22px !important;
  }

  /* ✅ wallet metrics stack */
  .wc-metrics {
    grid-template-columns: 1fr !important;
    gap: 10px !important;
  }

  /* ✅ add funds buttons -> column */
  .wc-actions {
    flex-direction: column !important;
    align-items: stretch !important;
    width: 100% !important;
  }

  .wc-actions .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* ✅ KPI cards -> 2 columns */
  .side-stats.grid.cols-2 {
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
  }

  /* ✅ table header stack */
  .table-head {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .table-head .btn.primary {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* ✅ mobile card internal rows */
  .row2 {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  /* ✅ mobile card buttons full width */
  .card-actions {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .card-actions .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* ✅ drawer full width */
  .drawer {
    width: 100% !important;
    max-width: 520px !important;
  }

  /* ✅ drawer qa buttons full width */
  .qa {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .qa .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* ✅ modal full width */
  .modal-backdrop {
    padding: 12px !important;
  }

  .modal {
    width: 100% !important;
    max-width: 560px !important;
    padding: 14px !important;
    border-radius: 22px !important;
  }

  .modal-foot {
    flex-direction: column-reverse !important;
    align-items: stretch !important;
  }

  .modal-foot .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }
}

/* ✅ extra small devices */
@media (max-width: 520px) {
  /* KPI cards become 1 column */
  .side-stats.grid.cols-2 {
    grid-template-columns: 1fr !important;
  }

  /* mobile cards top stacked */
  .w-card .top {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }
}
/* ✅ FIX: Select icon overlap inside modal input */
.input select.select.full {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  width: 100%;
  height: 48px;
  padding-left: 44px;   /* ✅ space for left icon */
  padding-right: 46px;  /* ✅ space for right dropdown arrow */
  border-radius: 18px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  font-size: 13px;
  font-weight: var(--w-medium);
  outline: none;
  cursor: pointer;
}

/* ✅ right dropdown arrow */
.input .dd {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  font-size: 18px;
  color: rgba(148, 163, 184, 0.95);
}

/* ✅ select focus same as inputs */
.input select.select.full:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

</style>
